import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useParams } from 'react-router-dom';
import type { Lead, LeadSource } from '@coachapp/shared';
import { FormPageLayout } from '../components/layout/FormPageLayout';
import { useTransitionNavigate } from '../hooks/useTransitionNavigate';
import { getApiErrorMessage } from '../lib/api-error';
import {
  useCreateLeadMutation,
  useGetLeadsQuery,
  useUpdateLeadMutation,
} from '../store/coachApi';

const SOURCES: LeadSource[] = ['INSTAGRAM', 'REFERRAL', 'WEBSITE', 'OTHER', 'UNKNOWN'];

interface LeadFormContentProps {
  initialData?: Lead;
  onClose: () => void;
  onCreated: () => void;
}

const LeadFormContent = ({ initialData, onClose, onCreated }: LeadFormContentProps) => {
  const { t } = useTranslation();
  const [error, setError] = useState('');
  const [createLead, { isLoading: creating }] = useCreateLeadMutation();
  const [updateLead, { isLoading: updating }] = useUpdateLeadMutation();
  const loading = creating || updating;

  const [formData, setFormData] = useState({
    name: initialData?.name || '',
    email: initialData?.email || '',
    phone: initialData?.phone || '',
    source: initialData?.source || 'UNKNOWN',
    notes: initialData?.notes || '',
    lostReason: initialData?.lostReason || '',
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    const payload = {
      name: formData.name,
      email: formData.email || undefined,
      phone: formData.phone || undefined,
      source: formData.source,
      notes: formData.notes || undefined,
      ...(initialData?.stage === 'LOST' && formData.lostReason
        ? { lostReason: formData.lostReason }
        : {}),
    };

    try {
      if (initialData) {
        await updateLead({ id: initialData.id, data: payload }).unwrap();
        onClose();
      } else {
        await createLead(payload).unwrap();
        onCreated();
      }
    } catch (err) {
      setError(
        err && typeof err === 'object' && 'message' in err && typeof err.message === 'string'
          ? err.message
          : getApiErrorMessage(err, 'Ошибка при сохранении'),
      );
    }
  };

  const pageTitle = initialData ? t('Редактировать потенциального клиента') : t('Новый потенциальный клиент');

  return (
    <FormPageLayout title={pageTitle} onBack={onClose}>
      <form onSubmit={handleSubmit} className="space-y-5">
        {error && (
          <div className="p-3 bg-red-500/10 border border-red-500/20 text-red-600 rounded-2xl text-center text-sm font-medium">
            {error}
          </div>
        )}

        <div className="space-y-1.5">
          <label className="text-sm font-semibold text-slate-800 ml-1">{t('Имя')}</label>
          <input
            type="text"
            className="input-field"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            required
          />
        </div>

        <div className="space-y-1.5">
          <label className="text-sm font-semibold text-slate-800 ml-1">{t('Электронная почта')}</label>
          <input
            type="email"
            className="input-field"
            value={formData.email}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
          />
        </div>

        <div className="space-y-1.5">
          <label className="text-sm font-semibold text-slate-800 ml-1">{t('Телефон')}</label>
          <input
            type="tel"
            className="input-field"
            value={formData.phone}
            onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
          />
        </div>

        <div className="space-y-1.5">
          <label className="text-sm font-semibold text-slate-800 ml-1">{t('Источник')}</label>
          <select
            className="input-field"
            value={formData.source}
            onChange={(e) => setFormData({ ...formData, source: e.target.value as LeadSource })}
          >
            {SOURCES.map((s) => (
              <option key={s} value={s}>
                {t(`leadSource.${s}`)}
              </option>
            ))}
          </select>
        </div>

        <div className="space-y-1.5">
          <label className="text-sm font-semibold text-slate-800 ml-1">{t('Заметки')}</label>
          <textarea
            className="input-field min-h-[80px] resize-y"
            value={formData.notes}
            onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
          />
        </div>

        {initialData?.stage === 'LOST' && (
          <div className="space-y-1.5">
            <label className="text-sm font-semibold text-slate-800 ml-1">{t('Причина потери')}</label>
            <input
              type="text"
              className="input-field"
              value={formData.lostReason}
              onChange={(e) => setFormData({ ...formData, lostReason: e.target.value })}
            />
          </div>
        )}

        <div className="flex flex-col-reverse sm:flex-row gap-3 pt-4">
          <button type="button" className="btn-secondary flex-1 min-h-12" onClick={onClose}>
            {t('Отмена')}
          </button>
          <button type="submit" className="btn-primary flex-1 sm:flex-[2] min-h-12" disabled={loading}>
            {loading ? t('Отправка...') : t('Сохранить')}
          </button>
        </div>
      </form>
    </FormPageLayout>
  );
};

const LeadFormRoute = () => {
  const navigate = useTransitionNavigate();
  const location = useLocation();
  const { leadId } = useParams();
  const { t } = useTranslation();
  const locationState = location.state as { lead?: Lead } | null;
  const { data: leads = [], isLoading } = useGetLeadsQuery(undefined, {
    skip: !leadId || !!locationState?.lead,
  });
  const initialData =
    locationState?.lead ?? (leadId ? leads.find((item) => item.id === leadId) : undefined);
  const detailLoading = !!leadId && !locationState?.lead && isLoading;
  const close = () => navigate(-1);
  const afterCreate = () => navigate('/clients?filter=leads', { replace: true });
  const pageTitle = initialData ? t('Редактировать потенциального клиента') : t('Новый потенциальный клиент');

  if (detailLoading) {
    return <FormPageLayout title={pageTitle} onBack={close} loading />;
  }

  return (
    <LeadFormContent
      key={initialData?.id ?? 'new'}
      initialData={initialData}
      onClose={close}
      onCreated={afterCreate}
    />
  );
};

export default LeadFormRoute;
